<script setup lang="ts">
import HeartEmpty from '@/assets/icons/2States/HeartEmpty.vue';
import EmployeesIcon from '@/assets/icons/Employees/EmployeesIcon.vue';
import PlanningIcon from '@/assets/icons/Home/PlanningIcon.vue';
import TagIcon from '@/assets/icons/Menus/TagIcon.vue';
import NavCreateIcon from '@/assets/icons/NavBar/NavCreateIcon.vue';
import NavMyRecipesIcon from '@/assets/icons/NavBar/NavMyRecipesIcon.vue';
import NavOrdersIcon from '@/assets/icons/NavBar/NavOrdersIcon.vue';
import NavTimeClockIcon from '@/assets/icons/NavBar/NavTimeClockIcon.vue';
import PlanningPostitsIcon from '@/assets/icons/Planning/PlanningPostitsIcon.vue';
import ScanIcon from '@/assets/icons/Profile/ScanIcon.vue';
import ShortcutButtonHomePage from '@/components/Home/ShortcutButtonHomePage.vue';
import LoadingBar from '@/components/Skeletons/LoadingBar.vue';
import { useSafeArea } from '@/composables/useSafeArea';
import type { MessageSchema } from '@/i18n';
import { useUserStore } from '@/stores/useUserStore';
import { hasSubscriptionFeature } from '@/utils/auth';
import { computed, ref, type Component } from 'vue';
import { useI18n } from 'vue-i18n';
import LoadingView from './LoadingView.vue';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const userStore = useUserStore();
const { fixedBottomStyle } = useSafeArea();

interface shortCutOptions {
  text: string;
  navigate: string;
  icon?: Component;
  disabled?: boolean;
  id?: string;
}

// Separate features for employees vs regular users
const employeeFeaturesOptions = computed<shortCutOptions[]>(() => [
  { text: t('homeView.employee.tasks'), navigate: '/planning?tab=2', icon: PlanningPostitsIcon },
  { text: t('homeView.employee.timetable'), navigate: '/planning?tab=1', icon: PlanningIcon },
  { text: t('homeView.employee.recipes'), navigate: '/my-recipes', icon: NavMyRecipesIcon },
  { text: t('homeView.employee.orders'), navigate: '/my-recipes', icon: NavOrdersIcon },
]);

const regularUserFeaturesOptions = computed<shortCutOptions[]>(() => [
  { text: t('homeView.myRecipes.myCategories'), navigate: '/my-recipes/tags', icon: TagIcon },
  { text: t('homeView.myRecipes.favourites'), navigate: '/my-recipes/favorites', icon: HeartEmpty },
  { text: t('homeView.planning.planning'), navigate: '/planning', icon: PlanningIcon },
  {
    text: t('homeView.planning.planningPeople'),
    navigate: '/profile/team',
    icon: EmployeesIcon,
    disabled: !hasSubscriptionFeature(userStore.user, 'employees_included', 1),
  },
]);

const featuresOptions = computed<shortCutOptions[]>(() => {
  return userStore.isEmployee ? employeeFeaturesOptions.value : regularUserFeaturesOptions.value;
});

const employeeMainFeaturesOptions = computed<shortCutOptions[]>(() => [
  { text: t('homeView.employee.timeclock'), navigate: '/timeclock', icon: NavTimeClockIcon },
  { text: t('homeView.planning.planning'), navigate: '/planning?tab=0', icon: PlanningIcon },
]);

const regularUserMainFeaturesOptions: shortCutOptions[] = [
  { text: t('homeView.myRecipes.scan'), navigate: '/my-recipes/my-scans', icon: ScanIcon, id: 'scan-recipes-button' },
  { text: t('homeView.create.generateRecipes'), navigate: '/menu/create', icon: NavCreateIcon, id: 'generate-recipes-button' },
];

const mainFeaturesOptions = computed<shortCutOptions[]>(() => {
  return userStore.isEmployee ? employeeMainFeaturesOptions.value : regularUserMainFeaturesOptions;
});

const isAnalyzing = ref(false);
</script>

<template>
  <main>
    <div v-if="isAnalyzing" class="p-6">
      <LoadingView :generation="true" />
      <LoadingBar class="mb-3 mt-24" :duration-in-seconds="40" />
    </div>
    <div v-else>
      <div id="home-header">
        <div class="relative">
          <div class="text-welcome">
            <h1 v-if="userStore.user" class="m-auto my-2 w-4/5 text-center">
              <span class="hello-container max-w-full truncate">{{ t('homeView.hello') }} {{ userStore.user.first_name }} !</span>
            </h1>
            <h1 v-else class="my-2 text-center">
              <span class="hello-container">{{ t('homeView.hello') }} !</span>
            </h1>
          </div>
          <img class="absolute top-0 -z-10 w-full rotate-180 opacity-10" src="@/assets/images/home-page-background.webp" />
        </div>
        <div class="avatar-image mt-4 text-center">
          <img src="@/assets/images/avatar-rounded.webp" :alt="t('homeView.altImageAvatar')" class="mx-auto w-1/4 max-w-32" />
        </div>
      </div>
      <div class="flex flex-col justify-between">
        <div id="onboarding-feature" class="absolute w-full max-w-[640px]" :style="fixedBottomStyle">
          <div class="px-6">
            <h3>
              {{ t('homeView.features') }}
            </h3>
          </div>
          <div class="relative mt-12 flex flex-col items-center gap-6 bg-gray-100 p-6">
            <div class="mt-28 inline-flex w-full max-w-80 items-start justify-between">
              <ShortcutButtonHomePage
                v-for="feature in featuresOptions"
                :id="feature.id"
                :key="feature.text"
                :icon="feature.icon"
                :text="feature.text"
                :navigate="feature.navigate"
                :disabled="feature.disabled"
                :small="true"
              />
            </div>
            <div class="absolute -top-10 flex flex-row content-between items-start justify-center gap-3">
              <ShortcutButtonHomePage
                v-for="mainFeature in mainFeaturesOptions"
                :id="mainFeature.id"
                :key="mainFeature.text"
                :icon="mainFeature.icon"
                :text="mainFeature.text"
                :navigate="mainFeature.navigate"
                :disabled="mainFeature.disabled"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped>
.hello-container {
  position: relative;
  display: inline-block;
}

.hello-container::after {
  content: '';
  position: absolute;
  bottom: 0px;
  right: 0px;
  width: 24%;
  height: 8px;
  background-color: #ed8d4c;
  opacity: 0.8;
  z-index: -1;
}

@media screen and (max-height: 520px) {
  .text-welcome {
    display: none;
  }
}
@media screen and (max-height: 663px) {
  .avatar-image {
    display: none;
  }
}

.hello-container {
  font-size: 1rem;
}

@media screen and (min-width: 360px) {
  .hello-container {
    font-size: 1.5rem;
  }
  .hello-container::after {
    content: '';
    position: absolute;
    bottom: 0px;
    right: 0px;
    width: 24%;
    height: 12px;
    background-color: #ed8d4c;
    opacity: 0.8;
    z-index: -1;
  }
}

@media screen and (min-width: 500px) {
  .hello-container {
    font-size: 1.75rem;
  }
  .hello-container::after {
    content: '';
    position: absolute;
    bottom: 0px;
    right: 0px;
    width: 24%;
    height: 14px;
    background-color: #ed8d4c;
    opacity: 0.8;
    z-index: -1;
  }
}
</style>
